import React, { useCallback, useContext, useEffect, useReducer, useState } from "react";
import * as notify from "shared/notify";

import Loading from "../../../components/callback/loading";
import {
    CODING_SHOT,
    DEFAULT_LOCALE,
    EDITING_SOURCE_MAP,
    getRemovableEmptySlots,
    mergePlaceholders,
    mergeShotsWithTemplate,
    sortByLocale,
    sortShots,
} from "../lib/helpers";

import {
    deleteImage,
    getPhotos,
    getShotTemplate,
    mapToSimplePhoto,
    uploadImage,
    updateImage as putImage,
    updateShot,
    saveGs1Code,
    rotateImage,
    swapShots,
    saveShotTemplate,
    mapTemplateOut,
    deleteMultipleImages,
    getEditingSources,
    copyShot,
} from "../lib/photoData";
import { ShotProvider } from "./index";
import { PhotographyContext } from "../../../contexts/photography";
import { TaskContext } from "../../../contexts/task";
import { useSidePanel } from "shared/hooks/use-side-panel";
import { reducer, initialState } from "../reducer";
import { arrayToObjectByKey } from "shared/helpers";
import { useUpdateZip } from "shared/hooks/api/use-update-zip";
import { useUploadZip } from "shared/hooks/api/use-upload-zip";

var shouldSaveTemplate = false;
var hasShotTemplate = false;

const PhotographyShots = ({ children, isCompleted, taskKey, photoWipKey }) => {
    const { mutate: updateZip } = useUpdateZip();
    const { mutate: uploadZip } = useUploadZip();
    const [shotsByLocale, dispatch] = useReducer(reducer, initialState);
    const { task } = useContext(TaskContext);
    const { lookups } = useContext(PhotographyContext);
    const { hasMobileBanner: hasHeroFeature } = useSidePanel();
    const [photos, setPhotos] = useState([]);
    const [isSwapping, setIsSwapping] = useState(false);
    const [copyInProgress, setCopyInProgress] = useState(false);
    const [shotTemplate, setShotTemplate] = useState(null);
    const [sourceShotKey, setSourceShotKey] = useState(null);
    const [copySourceShot, setCopySourceShot] = useState(null);
    const [selectedLocale, setSelectedLocale] = useState({ ...DEFAULT_LOCALE });
    const [pageState, setPageState] = useState({
        isLoaded: false,
        imageViewsByCode: {},
        localeTemplate: [],
        editingSources: [],
        defaultEditingSourceId: "",
        showDuplicateCheck: false,
        refresh: false,
    });
    const [packageTemplate, setPackageTemplate] = useState({
        defaultImageView: "",
        imageViews: [],
        imageViewsByCode: {},
        selectedId: "",
    });

    const fetchData = useCallback(
        async ({ refresh }) => {
            const photographyData = await getShotTemplate(photoWipKey);
            const photoData = await getPhotos(photoWipKey);
            const editingSources = await getEditingSources(task.context.hasPremiumEditing);
            const defaultEditingSource = editingSources.find((s) => s.isDropdownDefault === true);

            if (!photoData) {
                return;
            }

            const newShotTemplate = photographyData && photographyData.shotTemplate ? photographyData.shotTemplate : null;
            const newPackageType = photographyData && photographyData.packageType ? photographyData.packageType : "";

            //Actual Product Images
            // TODO: WTF?
            setPhotos(photoData);

            //Template Driven
            if (newPackageType !== packageTemplate.selectedId) {
                updatePackageType(photographyData.packageType || "", true);
            }

            if (newShotTemplate) {
                hasShotTemplate = true;
                setShotTemplate(newShotTemplate);
            }

            //PageState
            setPageState({
                editingSources,
                defaultEditingSourceId: defaultEditingSource ? defaultEditingSource.editingSourceId : "",
                isLoaded: true,
                refresh,
                imageViewsByCode: arrayToObjectByKey(lookups.imageViews, "imageViewCode"),
                localeTemplate: lookups.imageViews.filter((iv) => iv.isDefaultLocaleImageView),
                showDuplicateCheck: photoData.length === 0,
            });
        },
        [photoWipKey, task.context.hasPremiumEditing, packageTemplate.selectedId, lookups.imageViews]
    );

    const reloadImages = useCallback(() => fetchData({ refresh: true }), []);

    useEffect(() => {
        fetchData({ refresh: false });
    }, []);

    useEffect(() => {
        const populateTemplates = () => {
            const useTemplate = mergePlaceholders(
                shotTemplate,
                packageTemplate.imageViewsByCode,
                pageState.imageViewsByCode,
                selectedLocale.value,
                true
            );
            //PackageType changes causes refresh of primary language placeholders
            const shotsAndTemplates = isCompleted ? photos : mergeShotsWithTemplate(photos, useTemplate, selectedLocale.value || "");
            // Build shots from codes (packageType.imageViews > imageViews)
            const newShots = shotsAndTemplates
                .reduce((arr, shot) => {
                    const baseShot = packageTemplate.imageViewsByCode.hasOwnProperty(shot.imageViewCode)
                        ? packageTemplate.imageViewsByCode[shot.imageViewCode]
                        : pageState.imageViewsByCode[shot.imageViewCode];
                    arr.push(
                        mapToSimplePhoto({
                            ...baseShot,
                            ...shot,
                            localeCode: shot.localeCode || DEFAULT_LOCALE.value,
                        })
                    );

                    return arr;
                }, [])
                .sort(sortShots);

            dispatch({ type: "INIT", shots: sortByLocale(newShots) });
        };

        if (!pageState.isLoaded) {
            return;
        }

        if (pageState.refresh) {
            return populateTemplates();
        }

        populateTemplates();
        addDefaultHeroTemplate("MHB");
    }, [pageState.isLoaded, pageState.refresh]);

    useEffect(() => {
        if (shouldSaveTemplate) {
            saveTemplate(packageTemplate.selectedId);
            shouldSaveTemplate = false;
        }
    }, [shotsByLocale]);

    const getPlaceholder = (imageViewCode, defaultCoding = false) => {
        if (defaultCoding && (!imageViewCode || imageViewCode === CODING_SHOT.imageViewCode)) {
            return { ...CODING_SHOT };
        }

        return packageTemplate && packageTemplate.imageViewsByCode[imageViewCode]
            ? packageTemplate.imageViewsByCode[imageViewCode]
            : pageState.imageViewsByCode[imageViewCode];
    };

    const addDefaultHeroTemplate = (imageViewCode) => {
        if (!hasHeroFeature) {
            return;
        }

        const existingShot =
            (shotTemplate &&
                shotTemplate.find((s) => {
                    return s.imageViewCode === imageViewCode && s.localeCode === selectedLocale.value;
                })) ||
            photos.find((s) => {
                return s.imageViewCode === imageViewCode;
            });

        if (existingShot) {
            return;
        }

        const heroShot = getPlaceholder(imageViewCode);
        heroShot.canSkipEditing = false;
        heroShot.localeCode = selectedLocale.value;

        dispatch({ type: "ADD_PLACEHOLDERS", placeholders: [heroShot], localeCode: selectedLocale.value });
    };

    const saveTemplate = async () => {
        const minifiedShotTemplate = mapTemplateOut(shotsByLocale);
        const payload = {
            shotTemplate: minifiedShotTemplate,
            packageTypeId: packageTemplate.selectedId ? packageTemplate.selectedId : null, // Don't save packageType if not current one
        };
        const response = await saveShotTemplate(photoWipKey, payload, hasShotTemplate);

        if (response.success === false) {
            notify.error("You do not have permission to perform this action.");
        }

        if (response && !response.success) {
            hasShotTemplate = true;
        }

        setShotTemplate(minifiedShotTemplate);
    };

    const updatePackageType = (packageTypeId, ignoreSave) => {
        const packageType = packageTypeId ? lookups.packageTypes.find((t) => t.id === packageTypeId) : null;

        const templateMatch = packageType
            ? packageType
            : {
                  defaultImageView: { shortName: "" },
                  imageViews: [],
              };
        const defaultHeroShot = hasHeroFeature && pageState && pageState.imageViewsByCode["MHB"];
        const newImageViewList = defaultHeroShot ? [defaultHeroShot, ...templateMatch.imageViews] : templateMatch.imageViews;
        const packageImageViewsByCode = arrayToObjectByKey(newImageViewList, "imageViewCode");
        shouldSaveTemplate = !ignoreSave;
        setPackageTemplate({
            defaultImageView: templateMatch.defaultImageView.shortName,
            imageViews: newImageViewList,
            imageViewsByCode: arrayToObjectByKey(newImageViewList, "imageViewCode"),
            selectedId: packageTypeId || "",
        });

        const localePhotos = shotsByLocale[selectedLocale.value].filter((p) => !!p.key);
        const templateViews = newImageViewList.map((tv) => {
            return {
                ...tv,
                localeCode: DEFAULT_LOCALE.value,
            };
        });
        const shotsAndTemplates = isCompleted ? localePhotos : mergeShotsWithTemplate(localePhotos, templateViews, selectedLocale.value || "");
        // Build shots from codes (packageType.imageViews > imageViews)
        const localeShots = shotsAndTemplates
            .reduce((arr, shot) => {
                const baseShot = packageImageViewsByCode.hasOwnProperty(shot.imageViewCode)
                    ? packageImageViewsByCode[shot.imageViewCode]
                    : pageState.imageViewsByCode[shot.imageViewCode];
                const overrideProperties = {};

                if (
                    templateMatch.defaultImageView.shortName &&
                    templateMatch.defaultImageView.shortName === shot.imageViewCode &&
                    shot.editingSourceId === EDITING_SOURCE_MAP["skipEditing"]
                ) {
                    overrideProperties.editingSourceId = task.context.hasPremiumEditing
                        ? EDITING_SOURCE_MAP["brightRiverInternalPremium"]
                        : EDITING_SOURCE_MAP["brightRiver"];
                    updateProperty([shot.key], "editingSourceId", overrideProperties.editingSourceId, true);
                }

                arr.push(
                    mapToSimplePhoto({
                        ...baseShot,
                        ...shot,
                        localeCode: shot.localeCode || DEFAULT_LOCALE.value,
                        ...overrideProperties,
                    })
                );

                return arr;
            }, [])
            .sort(sortShots);

        dispatch({ type: "CHANGE_PACKAGE_TYPE", localeShots, localeCode: selectedLocale.value });
    };

    const addLocale = (newLocale) => {
        shouldSaveTemplate = true;
        dispatch({ type: "ADD_LOCALE", localeTemplate: pageState.localeTemplate || [], localeCode: newLocale.value });
    };

    const changeLocale = (newLocale) => {
        setSourceShotKey(null);
        setCopySourceShot(null);
        setSelectedLocale(newLocale);
    };

    const addSinglePlaceholder = (imageViewCode) => {
        const existingShot = shotsByLocale[selectedLocale.value].find((s) => {
            return s.imageViewCode === imageViewCode;
        });

        if (existingShot) {
            notify.error("Shot already exists");

            return;
        }

        const newPlaceholder = getPlaceholder(imageViewCode);
        newPlaceholder.canSkipEditing = false;
        newPlaceholder.localeCode = selectedLocale.value;

        shouldSaveTemplate = true;
        dispatch({ type: "ADD_PLACEHOLDERS", placeholders: [newPlaceholder], localeCode: selectedLocale.value });
        notify.success("Shot added to the list");
    };

    const addMultiplePlaceholders = async (list) => {
        if (list.length <= 0) {
            notify.error("No shots selected");

            return;
        }

        const shotsToAdd = [];

        list.forEach((shot) => {
            const existingShot = shotsByLocale[selectedLocale.value].find((s) => {
                return s.imageViewCode === shot.shortName;
            });

            if (existingShot) {
                return;
            }

            const newPlaceholder = getPlaceholder(shot.shortName);
            newPlaceholder.canSkipEditing = false;
            newPlaceholder.localeCode = selectedLocale.value;

            shotsToAdd.push({ ...newPlaceholder });
        });

        if (shotsToAdd.length > 0) {
            shouldSaveTemplate = true;
            dispatch({ type: "ADD_PLACEHOLDERS", placeholders: shotsToAdd, localeCode: selectedLocale.value });
            notify.success("Shots added to the list");
        } else {
            notify.error("Shots already added");
        }
    };

    const removePhoto = async (key, imageViewCode) => {
        if (key) {
            const response = await deleteImage(photoWipKey, taskKey, key);

            if (!response) {
                return;
            }

            // TODO: Potential sort issue here if you pull an image from one package, switch to another package and it re-adds the placeholder by the imageView
            const usePlaceholder = getPlaceholder(imageViewCode);

            dispatch({ type: "REMOVE_PHOTO", photoKey: key, placeholder: usePlaceholder, localeCode: selectedLocale.value });
        }
    };

    const removeShot = (imageViewCode) => {
        shouldSaveTemplate = true;
        dispatch({ type: "REMOVE_PLACEHOLDER", imageViewCode, localeCode: selectedLocale.value });
    };

    const removeEmptyShots = () => {
        const count = getRemovableEmptySlots(shotsByLocale, packageTemplate).length;

        if (count > 0) {
            shouldSaveTemplate = true;
            dispatch({ type: "REMOVE_EMPTY_PLACEHOLDERS", packageTemplate });
        }

        return count;
    };

    const updateZipFile = (zipFile, imageViewCode, shotKey) => {
        dispatch({ type: "SET_UPLOADING", isUploading: true, uploadsByKey: [shotKey], localeCode: selectedLocale.value });

        updateZip(
            { photoWipKey, shotKey, zipFile, imageViewCode },
            {
                onSuccess: (photo) => {
                    if (!photo) {
                        dispatch({ type: "SET_UPLOADING", isUploading: false, uploadsByKey: [shotKey], localeCode: selectedLocale.value });
                    }

                    dispatch({ type: "UPDATE_PHOTO", uploadedPhoto: photo, localeCode: selectedLocale.value });
                },
            }
        );
    };

    const uploadZipFile = (zipFile, viewCode) => {
        dispatch({ type: "SET_UPLOADING", isUploading: true, uploadsByShortcode: [viewCode], localeCode: selectedLocale.value });

        uploadZip(
            {
                photoWipKey,
                zipFile: zipFile[0],
                imageViewCode: viewCode,
                localeCode: selectedLocale.value,
            },
            {
                onSuccess: (zipShotResponse) => {
                    if (zipShotResponse && zipShotResponse.key) {
                        const usePlaceholder = getPlaceholder(zipShotResponse.imageViewCode, true);

                        dispatch({
                            type: "ADD_PHOTOS",
                            uploadedPhotos: [
                                {
                                    ...zipShotResponse,
                                    sortOrder: usePlaceholder ? usePlaceholder.sortOrder : null,
                                },
                            ],
                            localeCode: selectedLocale.value,
                        });
                    } else {
                        dispatch({ type: "SET_UPLOADING", isUploading: false, uploadsByShortcode: [viewCode], localeCode: selectedLocale.value });
                    }
                },
            }
        );
    };

    const uploadImages = (files, startAt) => {
        let availableShots;

        if (startAt) {
            // Rearrange availableShots to start with startAt (imageViewCode)
            let foundStart = false;
            availableShots = shotsByLocale[selectedLocale.value]
                .filter((s) => {
                    return !s.hasOwnProperty("key") && !s.isUploading && !s.isMultipleImageEligible;
                })
                .reduce((arr, shot) => {
                    if (foundStart || shot.imageViewCode === startAt) {
                        foundStart = true;
                        arr.push(shot);
                    }

                    return arr;
                }, []);
        } else {
            // Start from the beginning
            availableShots = shotsByLocale[selectedLocale.value].filter(
                (s) => !s.hasOwnProperty("key") && !s.isUploading && s.isMultipleImageEligible
            ); //TODO: isUploading?
        }

        const promises = [];
        const uploadsByShortcode = [];
        for (let f = 0; f < files.length; f++) {
            const formData = new FormData();
            const file = files[f];
            const imageViewCode = f < availableShots.length ? availableShots[f].imageViewCode : CODING_SHOT.imageViewCode;
            const isCoding = !imageViewCode || imageViewCode === CODING_SHOT.imageViewCode;
            const editingSourceId = isCoding
                ? EDITING_SOURCE_MAP["skipEditing"]
                : task.context.hasPremiumEditing
                  ? EDITING_SOURCE_MAP["brightRiverInternalPremium"]
                  : EDITING_SOURCE_MAP["brightRiver"];
            formData.append("file", file);
            formData.append("imageView", imageViewCode);
            formData.append("editingSourceId", editingSourceId);

            if (selectedLocale && selectedLocale.value) {
                formData.append("localeCode", selectedLocale.value);
            }

            if (isCoding) {
                formData.append("isUserAdded", true);
            }

            promises.push(uploadImage(photoWipKey, taskKey, formData));
            uploadsByShortcode.push(imageViewCode);
        }

        dispatch({ type: "SET_UPLOADING", isUploading: true, uploadsByShortcode, localeCode: selectedLocale.value });

        Promise.all(promises)
            .then((response) => {
                const successPhotoUploads = [];
                const failedPhotoUploads = [];
                response.forEach((shot) => {
                    if (shot && shot.key) {
                        // Only add if successful add
                        const usePlaceholder = getPlaceholder(shot.imageViewCode, true);
                        successPhotoUploads.push({
                            ...shot,
                            sortOrder: usePlaceholder ? usePlaceholder.sortOrder : null,
                        });
                    } else {
                        failedPhotoUploads.push(shot);
                    }
                });

                if (successPhotoUploads.length > 0) {
                    dispatch({ type: "ADD_PHOTOS", uploadedPhotos: successPhotoUploads, localeCode: selectedLocale.value });
                }

                if (failedPhotoUploads.length > 0) {
                    dispatch({ type: "SET_UPLOADING", isUploading: false, uploadsByShortcode, localeCode: selectedLocale.value });
                }
            })
            .catch(() => {
                dispatch({ type: "SET_UPLOADING", isUploading: false, localeCode: selectedLocale.value, error: true });
            });
    };

    const updateImage = async (formData, key) => {
        dispatch({ type: "SET_UPLOADING", isUploading: true, uploadsByKey: [key], localeCode: selectedLocale.value });

        const photo = await putImage(photoWipKey, taskKey, key, formData);

        if (!photo) {
            dispatch({ type: "SET_UPLOADING", isUploading: false, uploadsByKey: [key], localeCode: selectedLocale.value });

            return;
        }

        dispatch({ type: "UPDATE_PHOTO", uploadedPhoto: photo, localeCode: selectedLocale.value });
    };

    const updateGs1Code = async (formData, key) => {
        const payload = {
            gs1ImageTypeId: formData.imageType,
            gs1ImageFacingId: formData.imageFacing,
            gs1ImageAngleId: formData.imageAngle,
            gs1ImageStateId: formData.imageState,
        };
        const response = await saveGs1Code(key, payload);

        if (!response) {
            return;
        }

        notify.success("GS1 Code Saved");
        dispatch({
            type: "UPDATE_GS1_CODE",
            shotKey: response.shotKey,
            shortDescription: response.shortDescription,
            localeCode: selectedLocale.value,
        });
    };

    const rotateShot = async (key, rotate) => {
        dispatch({ type: "SET_ROTATING", rotateKeys: [key], isRotating: true, localeCode: selectedLocale.value });
        const response = await rotateImage(photoWipKey, key, rotate, 1);

        if (!response) {
            return;
        }

        notify.success("Rotation Saved");
        dispatch({ type: "SET_ROTATING", rotateKeys: [key], isRotating: false, localeCode: selectedLocale.value });
    };

    const handleSwap = async (selectedShot) => {
        setCopySourceShot(null);

        if (!selectedShot) {
            return;
        }

        if (isSwapping) {
            notify.error("Swap already in progress");

            return;
        }

        if (!sourceShotKey) {
            // Sets First Item to Swap
            setSourceShotKey(selectedShot.key);
        } else if (sourceShotKey === selectedShot.key) {
            // Cancel Swap if selecting the same item again
            setSourceShotKey(null);
        } else {
            const sourceShot = shotsByLocale[selectedLocale.value].find((s) => s.key === sourceShotKey);

            if (!sourceShot) {
                notify.error("Source shot missing");

                return;
            }

            if (sourceShot.imageViewCode === selectedShot.imageViewCode) {
                notify.error("Shot type must be different");

                return;
            }

            if (sourceShot.isRotating || selectedShot.isRotating) {
                notify.error("A rotation is currently in progress on one of the selected shots");

                return;
            }

            setIsSwapping(true);
            //dispatch({ type: 'SET_UPLOADING', isUploading: true, uploadsByShortcode: [selectedShot.imageViewCode, sourceShot.imageViewCode], localeCode: selectedLocale.value });

            const payload = {
                sourceShotKey: sourceShot.key,
                destinationShotKey: selectedShot.key || null,
                destinationShortCode: selectedShot.imageViewCode,
                localeCode: selectedLocale.value,
                swapUneditedShot: true,
                swapEditedShot: true,
            };
            const swappedShots = await swapShots(photoWipKey, payload);
            setIsSwapping(false);

            if (!swappedShots) {
                return;
            }

            notify.success("Image swap saved");

            const replacementShots = swappedShots.reduce((arr, s) => {
                if (!s) {
                    const handleNull = pageState.imageViewsByCode[sourceShot.imageViewCode];
                    arr.push({
                        ...handleNull,
                        localeCode: selectedLocale.value,
                    });

                    return arr;
                }

                const usePlaceholder =
                    packageTemplate && packageTemplate.imageViewsByCode[s.imageViewCode]
                        ? packageTemplate.imageViewsByCode[s.imageViewCode]
                        : pageState.imageViewsByCode[s.imageViewCode] || { ...CODING_SHOT };

                // Handle Coding Shots
                if (s.imageViewCode === CODING_SHOT.imageViewCode) {
                    // If image
                    if (s.uneditedBlobKey) {
                        arr.push({
                            ...s,
                            ...CODING_SHOT,
                            localeCode: selectedLocale.value,
                            sortOrder: usePlaceholder.sortOrder,
                        });
                    }

                    //Else don't include
                    return arr;
                }

                // Replace shot with placeholder if the imageViewCode doesn't have an image
                if (!s.uneditedBlobKey) {
                    arr.push({
                        ...usePlaceholder,
                        localeCode: selectedLocale.value,
                        sortOrder: usePlaceholder.sortOrder,
                    });

                    return arr;
                }

                // Both have images, send 'em both
                arr.push({
                    ...s,
                    localeCode: selectedLocale.value,
                    sortOrder: usePlaceholder.sortOrder,
                });

                return arr;
            }, []);

            const removeKeys = [sourceShot.key];

            if (selectedShot.key) {
                removeKeys.push(selectedShot.key);
            }

            dispatch({
                type: "SWAP_PHOTOS",
                replacementShots,
                removeImageViewCodes: [sourceShot.imageViewCode, selectedShot.imageViewCode],
                removeKeys: removeKeys,
                localeCode: selectedLocale.value,
            });
            setSourceShotKey(null);
        }
    };

    const handleCopy = async (selectedShot) => {
        setSourceShotKey(null);

        if (!selectedShot) {
            return;
        }

        if (copyInProgress) {
            notify.error("Copy already in progress");

            return;
        }

        if (!copySourceShot) {
            // Sets First Item to Swap
            setCopySourceShot({ key: selectedShot.key, imageView: selectedShot.imageView });
        } else if (copySourceShot.key === selectedShot.key) {
            // Cancel Swap if selecting the same item again
            setCopySourceShot(null);
        } else {
            const sourceShot = shotsByLocale[selectedLocale.value].find((s) => s.key === copySourceShot.key);

            if (!sourceShot) {
                notify.error("Source shot missing");

                return;
            }

            if (sourceShot.imageViewCode === selectedShot.imageViewCode) {
                notify.error("Shot type must be different");

                return;
            }

            if (sourceShot.isRotating || selectedShot.isRotating) {
                notify.error("A rotation is currently in progress on one of the selected shots");

                return;
            }

            const isReplacingExistingShot = !!selectedShot.key;

            if (isReplacingExistingShot) {
                const isAccepted = await notify.confirm(`Are you sure you want the copied image to replace the existing image?`);

                if (!isAccepted) {
                    setSourceShotKey(null);
                    setCopySourceShot(null);

                    return;
                }
            }

            setCopyInProgress(true);

            dispatch({
                type: "COPY_SHOT_START",
                isCopyingToEmptyCodingPlaceholder: !selectedShot.key && selectedShot.imageViewCode === CODING_SHOT.imageViewCode,
                targetShotKey: selectedShot.key,
                imageViewCode: selectedShot.imageViewCode,
                localeCode: selectedLocale.value,
            });

            const copiedShot = await copyShot(photoWipKey, copySourceShot.key, {
                targetImageView: selectedShot.imageViewCode,
                targetShotKey: selectedShot.key,
            });

            setCopyInProgress(false);

            notify.success("Image copied");

            dispatch({
                type: "COPY_SHOT_FINISH",
                id: selectedShot.id,
                isCopyingToEmptyCodingPlaceholder: !selectedShot.key && selectedShot.imageViewCode === CODING_SHOT.imageViewCode,
                copiedShot: mapToSimplePhoto(copiedShot),
                showGs1Codes: sourceShot.hasGs1Metadata,
                imageCodeView: selectedShot.imageViewCode,
                sourceWipShotKey: copiedShot.sourceWipShotKey,
                localeCode: selectedLocale.value,
            });

            setCopySourceShot(null);
        }
    };

    const updateProperty = async (shotKeys, name, value, save = true) => {
        if (save) {
            const promises = shotKeys.reduce((arr, shotKey) => {
                arr.push(updateShot(photoWipKey, taskKey, shotKey, task.sourcePage, { [name]: value }));

                return arr;
            }, []);

            Promise.all(promises).then((response) => {
                const errors = [];
                var successCount = 0;
                response.forEach((r) => {
                    if (!r) {
                        return;
                    }

                    if (r.errors) {
                        r.errors.forEach((e) => {
                            if (e.message) {
                                errors.push(e.message);
                            }
                        });
                    } else {
                        successCount = successCount + 1;
                    }
                });

                if (errors.length > 0) {
                    notify.error(errors.join("<br />"));
                }

                if (successCount > 0) {
                    notify.success(`${successCount} updated`);
                }
            });
        }

        dispatch({ type: "SET_PROPERTIES", shotKeys: shotKeys, property: name, value, localeCode: selectedLocale.value });
    };

    const removeByLocaleCode = async (localeCode) => {
        if (DEFAULT_LOCALE.value === localeCode) {
            notify.error(`${localeCode} is required and can not be deleted.`);

            return;
        }

        const confirm = await notify.confirm("Are you sure you want to remove this locale and all uploaded?");

        if (!confirm) {
            return;
        }

        const isBusy = shotsByLocale[localeCode].some((s) => s.isUploading);

        if (isBusy) {
            notify.error("Upload in progress, please try again later.");

            return;
        }

        const shotKeys = shotsByLocale[localeCode].reduce((arr, s) => {
            if (s.key) {
                arr.push(s.key);
            }

            return arr;
        }, []);
        const response = await deleteMultipleImages(photoWipKey, taskKey, shotKeys);

        if (!response) {
            return;
        }

        shouldSaveTemplate = true;
        dispatch({ type: "REMOVE_LOCALE", localeCode });
        setSelectedLocale({ ...DEFAULT_LOCALE });
    };

    if (!pageState.isLoaded) {
        return <Loading />;
    }

    return (
        <ShotProvider
            value={{
                addLocale,
                addMultiplePlaceholders,
                addSinglePlaceholder,
                changeLocale,
                editingSources: pageState.editingSources,
                handleSwap,
                pageState,
                packageTemplate,
                removeByLocaleCode,
                removeEmptyShots,
                removePhoto,
                removeShot,
                rotateShot,
                selectedLocale,
                sourceShotKey,
                shotsByLocale,
                updateImage,
                updateGs1Code,
                uploadImages,
                uploadZipFile,
                updateZipFile,
                updatePackageType,
                updateProperty,
                refreshImages: reloadImages,
                handleCopy,
                copySourceShot,
            }}
        >
            {children}
        </ShotProvider>
    );
};

export default PhotographyShots;
